import React, { useCallback, useState, useEffect } from 'react';
import Hls from 'hls.js';
import { CTAs } from '../../index';
import CTAStyl from './CTA.styl';

export type CTAProps = {
  hls: Hls;
  ctas: CTAs[];
  code: string;
  enabled: boolean;
};

const CTA = ({ hls, ctas, code, enabled }: CTAProps) => {
  const [visibleCTAs, setVisibleCTAs] = useState<CTAs[]>([]);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const checkTimeAndUpdateVisibleCTAs = useCallback(() => {
    if (!hls || !hls.media || !enabled || !ctas.length) {
      setVisibleCTAs([]);
      return;
    }

    const currentTime = hls.media.currentTime;

    // Filtrar CTAs que devem estar visíveis no tempo atual
    const shouldBeVisible = ctas.filter(cta =>
      currentTime >= cta.startTime && currentTime <= cta.endTime
    );

    setVisibleCTAs(shouldBeVisible);
  }, [hls, ctas, enabled, ctas]);

  const handleCTAClick = useCallback((cta: CTAs, event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Abrir link em nova aba
    if (cta.link) {
      window.open(cta.link, '_blank', 'noopener,noreferrer');
    }
  }, []);

  useEffect(() => {
    if (!hls || !hls.media || !enabled) {
      return;
    }

    const video = hls.media;

    // Usar múltiplos eventos para garantir funcionamento
    video.addEventListener('timeupdate', checkTimeAndUpdateVisibleCTAs);
    video.addEventListener('play', checkTimeAndUpdateVisibleCTAs);
    video.addEventListener('playing', checkTimeAndUpdateVisibleCTAs);
    video.addEventListener('pause', checkTimeAndUpdateVisibleCTAs);
    video.addEventListener('seeked', checkTimeAndUpdateVisibleCTAs);
    video.addEventListener('loadedmetadata', checkTimeAndUpdateVisibleCTAs);

    // Polling para garantir funcionamento
    intervalRef.current = setInterval(checkTimeAndUpdateVisibleCTAs, 500);

    // Verificar imediatamente
    checkTimeAndUpdateVisibleCTAs();

    return () => {
      video.removeEventListener('timeupdate', checkTimeAndUpdateVisibleCTAs);
      video.removeEventListener('play', checkTimeAndUpdateVisibleCTAs);
      video.removeEventListener('playing', checkTimeAndUpdateVisibleCTAs);
      video.removeEventListener('pause', checkTimeAndUpdateVisibleCTAs);
      video.removeEventListener('seeked', checkTimeAndUpdateVisibleCTAs);
      video.removeEventListener('loadedmetadata', checkTimeAndUpdateVisibleCTAs);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hls, hls?.media, checkTimeAndUpdateVisibleCTAs, enabled, ctas]);

  if (!enabled || !visibleCTAs.length) {
    return null;
  }

  return (
    <CTAStyl>
      {visibleCTAs.map((cta, index) => (
        <div
          key={`${cta.startTime}-${cta.endTime}-${index}`}
          className={`smartplayer__cta smartplayer__cta--${cta.position} smartplayer__cta--${cta.size}`}
        >
          <a
            href={cta.link}
            className="smartplayer__ctaButton"
            onClick={(event) => handleCTAClick(cta, event)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: cta.bgColor,
              color: cta.textColor,
            }}
            title={cta.text}
          >
            <span className="smartplayer__ctaText">
              {cta.text}
            </span>
          </a>
        </div>
      ))}
    </CTAStyl>
  );
};

export default CTA;
